import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Shield, Phone, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import { getSuburbSuggestions } from "@/data/australianSuburbs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BrowseTradies() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tradies, setTradies] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Location autocomplete state
  const [locationInput, setLocationInput] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  
  const [filters, setFilters] = useState({
    service: searchParams.get('service') || '',
    postcode: searchParams.get('postcode') || '',
    suburb: searchParams.get('suburb') || ''
  });

  // Initialize location input from URL params
  useEffect(() => {
    const suburb = searchParams.get('suburb');
    const postcode = searchParams.get('postcode');
    if (suburb && postcode) {
      setLocationInput(`${suburb} ${postcode}`);
    } else if (suburb) {
      setLocationInput(suburb);
    } else if (postcode) {
      setLocationInput(postcode);
    }
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    // Only fetch tradies if there are search params
    const hasSearchParams = searchParams.get('service') || searchParams.get('suburb') || searchParams.get('postcode');
    if (hasSearchParams) {
      fetchTradies();
    } else {
      setLoading(false);
      setTradies([]);
    }
  }, [searchParams]);

  const fetchTradies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const service = searchParams.get('service');
      const postcode = searchParams.get('postcode');
      const suburb = searchParams.get('suburb');
      
      if (service && service !== 'all') params.append('service', service);
      if (postcode) params.append('postcode', postcode);
      if (suburb) params.append('suburb', suburb);

      const response = await axios.get(`${API}/tradies?${params.toString()}`);
      setTradies(response.data);
    } catch (error) {
      console.error("Error fetching tradies:", error);
      toast.error("Failed to load tradies");
    } finally {
      setLoading(false);
    }
  };

  const handleLocationInputChange = (value) => {
    setLocationInput(value);
    
    if (value.length >= 2) {
      const suggestions = getSuburbSuggestions(value);
      setLocationSuggestions(suggestions);
      setShowLocationSuggestions(suggestions.length > 0);
    } else {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };

  const handleLocationSelect = (suburb, postcode, state) => {
    setLocationInput(`${suburb} ${postcode}`);
    setFilters({
      ...filters,
      suburb: suburb,
      postcode: postcode
    });
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (filters.service && filters.service !== 'all') params.append('service', filters.service);
    
    // Use filters if set via autocomplete, otherwise use the raw locationInput
    if (filters.postcode) {
      params.append('postcode', filters.postcode);
    }
    if (filters.suburb) {
      params.append('suburb', filters.suburb);
    } else if (locationInput.trim()) {
      // User typed a location but didn't select from autocomplete
      // Try to parse it - could be "Suburb Postcode" format or just suburb name
      const trimmedInput = locationInput.trim();
      const postcodeMatch = trimmedInput.match(/\d{4}$/);
      if (postcodeMatch) {
        // Extract postcode from end
        const postcode = postcodeMatch[0];
        const suburb = trimmedInput.replace(postcode, '').trim();
        if (suburb) params.append('suburb', suburb);
        params.append('postcode', postcode);
      } else {
        // Treat entire input as suburb name
        params.append('suburb', trimmedInput);
      }
    }
    setSearchParams(params);
  };

  const getServiceName = (slug) => {
    const service = services.find(s => s.slug === slug);
    return service ? service.name : slug;
  };

  // Generate SEO-friendly URL for a tradie
  const getTradieUrl = (tradie) => {
    if (tradie.slug) {
      // Use new SEO format: /tradies/{location}/{service}/{slug}
      const location = tradie.suburb.toLowerCase().replace(/\s+/g, '-');
      const service = tradie.services?.[0] || 'tradie';
      return `/tradies/${location}/${service}/${tradie.slug}`;
    }
    // Fallback to legacy UUID format
    return `/tradie/${tradie.id}`;
  };

  // Build dynamic SEO titles - keyword rich
  const serviceName = filters.service ? getServiceName(filters.service) : '';
  const locationName = filters.suburb || '';
  
  // Format service name for display (e.g., "carpenter" -> "Carpenters")
  const serviceDisplayName = serviceName ? 
    (serviceName.endsWith('s') ? serviceName : serviceName + 's') : '';
  
  // Build SEO-optimized titles
  const hasFilters = serviceName || locationName;
  const seoTitle = hasFilters 
    ? `${serviceDisplayName} ${locationName} | Quality ${serviceName} by ${locationName} Tradies`
    : 'Find Local Tradies Across Australia | Local Tradies Online';
  
  const seoDescription = hasFilters
    ? `Find trusted ${serviceDisplayName.toLowerCase()} in ${locationName}. Quality ${serviceName.toLowerCase()} services by verified ${locationName} tradies. Get free quotes today.`
    : 'Find trusted local tradies across Australia. Electricians, plumbers, builders and more. Get free quotes from verified professionals.';
  
  // H1 heading text
  const h1Title = hasFilters 
    ? `${serviceDisplayName} ${locationName}`
    : 'Search for Tradies';
  
  const h1Subtitle = hasFilters
    ? `Quality ${serviceName} by ${locationName} Tradies`
    : 'Select a trade or service above and enter your location to find local tradies in your area';

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={`https://localtradiesonline.com.au/browse${window.location.search}`} />
      </Helmet>
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Header - SEO Optimized */}
          <div className="mb-8">
            <h1 
              className="text-4xl md:text-5xl font-bold tracking-tight text-[#0f172a] mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="browse-heading"
            >
              {h1Title}
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-slate-600">
              {h1Subtitle}
            </p>
          </div>

          {/* Search Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8" data-testid="search-filters">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select 
                  value={filters.service} 
                  onValueChange={(value) => setFilters({...filters, service: value})}
                >
                  <SelectTrigger className="h-12" data-testid="filter-service-select">
                    <SelectValue placeholder="Search Trades" />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    <SelectItem value="all">All Services</SelectItem>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.slug}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Smart Location Input with Autocomplete */}
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Location"
                    value={locationInput}
                    onChange={(e) => handleLocationInputChange(e.target.value)}
                    onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                    onFocus={() => locationInput.length >= 2 && locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
                    className="h-12"
                    data-testid="filter-location-input"
                  />
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {locationSuggestions.map((item, index) => (
                        <div
                          key={index}
                          className="px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                          onMouseDown={() => handleLocationSelect(item.suburb, item.postcode, item.state)}
                          data-testid={`location-suggestion-${index}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 text-[#f97316] mr-2" />
                              <span className="font-medium text-[#0f172a]">{item.suburb}</span>
                              <span className="text-slate-500 ml-2">{item.state}</span>
                            </div>
                            <span className="text-sm font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{item.postcode}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="h-12 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold"
                  data-testid="search-btn"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </Button>
              </div>
            </form>
          </div>

          {/* Results */}
          {(searchParams.get('service') || searchParams.get('suburb') || searchParams.get('postcode')) && (
            <div className="mb-4">
              <p className="text-slate-600" data-testid="results-count">
                {loading ? 'Loading...' : `${tradies.length} tradies found`}
              </p>
            </div>
          )}

          {/* Tradies Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#f97316] border-r-transparent"></div>
            </div>
          ) : !searchParams.get('service') && !searchParams.get('suburb') && !searchParams.get('postcode') ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center" data-testid="search-prompt">
              <div className="max-w-md mx-auto">
                <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Search for Tradies
                </h2>
                <p className="text-slate-600 mb-6">
                  Select a trade or service above and enter your location to find local tradies in your area.
                </p>
              </div>
            </div>
          ) : tradies.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center" data-testid="no-results">
              <p className="text-lg text-slate-600 mb-4">No tradies found matching your criteria.</p>
              <Link 
                to="/get-quote"
                className="inline-block text-[#f97316] hover:text-[#ea580c] font-bold"
              >
                Submit a quote request instead
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tradies-grid">
              {tradies.map((tradie) => (
                <Link
                  key={tradie.id}
                  to={getTradieUrl(tradie)}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`tradie-card-${tradie.id}`}
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-xl text-[#0f172a] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {tradie.business_name}
                        </h3>
                        {/* Only show contact name for verified tradies */}
                        {tradie.verified && tradie.contact_name && (
                          <p className="text-sm text-slate-600">{tradie.contact_name}</p>
                        )}
                      </div>
                      {tradie.verified && (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          <Shield className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>

                    {/* Rating */}
                    {tradie.rating > 0 && (
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="flex items-center">
                          <Star className="w-4 h-4 fill-orange-400 text-orange-400" />
                          <span className="ml-1 font-semibold text-[#0f172a]">{tradie.rating.toFixed(1)}</span>
                        </div>
                        <span className="text-sm text-slate-500">rating</span>
                      </div>
                    )}

                    {/* Services */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {tradie.services.slice(0, 3).map((serviceSlug, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {getServiceName(serviceSlug)}
                          </Badge>
                        ))}
                        {tradie.services.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{tradie.services.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-center text-sm text-slate-600 mb-4">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{tradie.suburb}, {tradie.state} {tradie.postcode}</span>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                      {tradie.description}
                    </p>

                    {/* CTA */}
                    <Button 
                      className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-bold"
                      data-testid={`view-profile-btn-${tradie.id}`}
                    >
                      View Profile & Contact
                    </Button>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}